import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

/**
 * Componente Select reutilizable con diseño consistente con Input
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>((
  { label, error, options, placeholder = 'Seleccionar...', className = '', value, ...props },
  ref
) => {
  const hasValue = value && value !== '';
  
  const selectClasses = `
    w-full px-4 py-3 
    bg-secondary-700 border-0
    rounded-lg placeholder-secondary-400
    focus:outline-none focus:ring-4 focus:ring-primary-500
    transition-all duration-200 ease-in-out
    disabled:opacity-50 disabled:cursor-not-allowed
    text-base
    hover:bg-secondary-600
    appearance-none
    cursor-pointer
    ${error ? 'focus:ring-red-500' : ''}
    ${hasValue ? 'text-primary-400 font-medium' : 'text-secondary-50'}
    ${className}
  `;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-secondary-50 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          className={selectClasses}
          value={value}
          {...props}
        >
          <option value="" className="text-secondary-400 bg-secondary-700">
            {placeholder}
          </option>
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              className="bg-secondary-700 text-secondary-50 hover:bg-primary-500 hover:text-white"
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className={`absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none transition-colors ${
          hasValue ? 'text-primary-400' : 'text-secondary-400'
        }`}>
          <ChevronDown className="h-5 w-5" />
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-400 font-medium">{error}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';
export default Select;